import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as _ from 'lodash';
import i18n from 'i18n-js';
import { Table, Popup, Icon, Message } from 'semantic-grab-ui/src';
import moment from 'moment';
import { Progress } from '@duxton-web/ui';

import { Campaign } from 'types/ts/campaigns';
import { getTimeProgress } from 'omega-rtc/actions/newTridentExtra';
import { getProgressPercent } from 'omega-rtc/utils/new-trident-utils';
import { getSegmentName } from './constants/utils';
import { TwoCellRow } from 'omega-rtc/components/custom';
import { SCHEDULE_JOB_STATUS } from 'constants/schedule';

// Constants moved outside component
const EXPECTED_MAX_MESSAGE_DELAY_HOURS = 4000;

interface Props {
  model: Campaign;
  currentNode: string;
  parentNode: string;
}

const EnumerationShow: React.FC<Props> = ({
  model,
  currentNode,
  parentNode,
}) => {
  const dispatch = useDispatch();
  // Separate useSelector calls
  const progress = useSelector((state) =>
    _.get(state, 'newTridents.progress', {}),
  );
  const currentNodeProgressKey = _.get(
    model,
    `rootNodeScheduleMapping[${parentNode}]`,
    0,
  );
  const currentNodeProgress = _.get(progress, `${currentNodeProgressKey}`, {});

  // Memoized values
  const data = useMemo(
    () => _.get(model, `nodes[${currentNode}].data`, {}),
    [model, currentNode],
  );
  const lastHappenedAt = useMemo(
    () => _.get(model, `nodes[${parentNode}].data.lastHappenedAt`),
    [model, parentNode],
  );

  const {
    processedCount = 0,
    totalCount = 0,
    reports = [],
    status = '', // 'Active' or 'Done' or 'Failed'
    errMsg = '', // error message if the latest trigger failed
  } = currentNodeProgress;

  const progressPercent = useMemo(
    () => getProgressPercent(processedCount, totalCount),
    [processedCount, totalCount],
  );

  const hoursSinceLastTrigger = useMemo(
    () =>
      (!_.isEmpty(lastHappenedAt)
        ? moment().diff(lastHappenedAt, 'hours')
        : null),
    [lastHappenedAt],
  );

  useEffect(() => {
    if (!_.isEmpty(lastHappenedAt)) {
      const scheduleID = _.get(
        model,
        `rootNodeScheduleMapping[${parentNode}]`,
        0,
      );
      dispatch(
        getTimeProgress({
          scheduleID,
          triggerTime: lastHappenedAt.format(),
        }),
      );
    }
  }, [lastHappenedAt, model, parentNode, getTimeProgress]);

  const errorMsg = _.get(model, `nodes[${parentNode}].data.errorMsg`, '').slice(
    0,
    100,
  );
  const errorAt = _.get(model, `nodes[${parentNode}].data.errorAt`, '');
  const showEmptySegmentWarning = errorAt && !errorMsg && totalCount === 0;

  const enumType = _.get(data, 'type', 'segment');
  const payloadDisplay = getSegmentName(_.get(data, 'payload.names.0', '')); // for now we only support one segment as enumeration payload, in the future we might support other types of enumeration

  let downloadReportHTML = <p>Report will be generated soon ...</p>;
  if (reports.length === 1) {
    downloadReportHTML = (
      <a href={_.get(reports[0], 'path')} download>
        {' '}
        <Icon name="download" style={{ fontSize: '1.5em' }} />{' '}
      </a>
    );
  } else if (reports.length > 1) {
    downloadReportHTML = (
      <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
        {reports.map((report, index) => (
          <li key={index} style={{ marginTop: 10 }}>
            <a href={_.get(report, 'path')} download>
              {' '}
              <Icon name="download" style={{ fontSize: '1.5em' }} />{' '}
            </a>
            {moment(_.get(report, 'triggeredTime')).format(
              'DD-MM-YYYY hh:mm a',
            )}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Table striped>
      <Table.Body>
        <Table.Row>
          <Table.Cell>
            <span>Type</span>
          </Table.Cell>
          <Table.Cell>
            <span>{enumType}</span>
          </Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>
            <span>Payload</span>
          </Table.Cell>
          <Table.Cell>
            <span>{payloadDisplay}</span>
          </Table.Cell>
        </Table.Row>

        {progressPercent >= 100 && (
          <TwoCellRow
            firstCell={i18n.t('common.download_report')}
            secondCell={downloadReportHTML}
          />
        )}

        {totalCount !== -1 && (
          <TwoCellRow
            firstCell={i18n.t('targets_page.fields.progress')}
            secondCell={
              <React.Fragment>
                {progressPercent >= 100 &&
                  hoursSinceLastTrigger <= EXPECTED_MAX_MESSAGE_DELAY_HOURS && (
                    <Popup
                      trigger={
                        <Message info>
                          If you are using message actions, please note that
                          message sending might still be in progress.
                        </Message>
                      }
                      content={
                        "This is because message sending is async on hedwig's side. The actual time the message gets delivered will depend on hedwig's system load. "
                      }
                    />
                  )}

                <Popup
                  trigger={
                    <Progress
                      percent={showEmptySegmentWarning ? 100 : progressPercent}
                      showInfo
                    />
                  }
                  content={`Processed Count: ${processedCount}  Total Count: ${totalCount}`}
                />
              </React.Fragment>
            }
          />
        )}

        {status === SCHEDULE_JOB_STATUS.DONE && (
          <TwoCellRow
            firstCell={<span>Status</span>}
            secondCell={
              <Message positive>
                <span style={{ color: 'green' }}>Completed</span>
              </Message>
            }
          />
        )}

        {status === SCHEDULE_JOB_STATUS.FAILED && (
          <TwoCellRow
            firstCell={<span>Status</span>}
            secondCell={
              <Message negative>
                <span style={{ color: 'red' }}>Failed</span>
                {errMsg && (
                  <div style={{ marginTop: '5px', fontSize: '0.9em' }}>
                    {errMsg}
                  </div>
                )}
              </Message>
            }
          />
        )}

        {showEmptySegmentWarning && (
          <Table.Row>
            <Table.Cell>
              <span>Warning</span>
            </Table.Cell>
            <Table.Cell>
              <span>Schedule is processed with empty segment</span>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
  );
};

export default EnumerationShow;
